import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {FetchPolicy} from "@khanacademy/wonder-blocks-data";
import {PRODUCT_BY_ID_QUERY, type Referral, type Role} from "@khan/auth-shared";
import {useQuery} from "@khan/data-gql";
import {RequestInfo} from "@khan/shared-core";
import {mediaQueries} from "@khan/shared-styles";
import {cssUrl} from "@khan/urls";

import TermsOfServiceSignupStatement from "#auth/components/terms-of-service-signup-statement.tsx";
import {
    isKhanmigoReferral,
    isLearnStormReferral,
} from "#auth/components/util.ts";

import ProgressShellDetailScreen from "../progress-shell/progress-shell-detail-screen.tsx";

type Copy = {
    referral?: [Referral];
    header?: string;
    description: Array<React.ReactNode>;
};

type RoleCopy = {
    role: Role;
    copy: Array<Copy>;
};
type RoleCopies = Array<RoleCopy>;

function getRoleCopies(monthlyCost: string, yearlyCost: string) {
    const DEFAULT_COPY: Copy = {
        referral: ["default"],
        header: t`Join Khan Academy to activate your learning`,
        description: [t`Log in to Khan Academy to get started!`],
    };

    return [
        {
            role: "learner",
            copy: [
                DEFAULT_COPY,
                {
                    referral: ["KhanmigoCheckout", "KhanmigoSignup"],
                    header: t`Ready to get AI-powered tutoring?`,
                    description: [
                        // Only show the cost if we have it
                        monthlyCost && yearlyCost
                            ? t`Get access to Khanmigo’s personalized learning for $${monthlyCost}/month or $${yearlyCost}/year and level up your learning today.`
                            : t`Get access to Khanmigo’s personalized learning and level up your learning today.`,
                    ],
                },
            ],
        },
        {
            role: "teacher",
            copy: [
                {
                    ...DEFAULT_COPY,
                    header: t`Join Khan Academy to activate your teaching`,
                },
                {
                    referral: ["LearnStorm"],
                    description: [
                        t`To enroll for LearnStorm, you’ll need to create a teacher account on Khan Academy. Once you create an account, we’ll take you through a short overview of our tools. Don’t forget to enroll for LearnStorm at the end of the overview.`,
                    ],
                },
                {
                    referral: ["KhanmigoCheckout"],
                    header: t`Prep your week with an AI-powered toolkit.`,
                    description: [
                        // Only show the cost if we have it
                        monthlyCost && yearlyCost
                            ? t`Adapt your curriculum, build lesson plans, and more for only $${monthlyCost}/month or $${yearlyCost}/year to start streamlining your workflow today.`
                            : t`Adapt your curriculum, build lesson plans, and more to start streamlining your workflow today.`,
                    ],
                },
                {
                    referral: ["KhanmigoSignup"],
                    header: t`Knock prep off your to-do list in minutes.`,
                    description: [
                        t`Spend more time teaching and connecting with students. Completely 100% free.`,
                    ],
                },
            ],
        },
        {
            role: "parent",
            copy: [
                {
                    ...DEFAULT_COPY,
                    header: t`Support your child’s learning through Khan Academy`,
                },
                {
                    referral: ["parent_approval_success"],
                    description: [
                        t`Thank you for approving your child's account! Next, create your parent account.`,
                    ],
                },
                {
                    referral: ["parent_approval_failed"],
                    description: [t`Your child's account has expired.`],
                },
                {
                    referral: ["KhanmigoCheckout", "KhanmigoSignup"],
                    header: t`Your child’s safe, reliable AI-powered tutor is here.`,
                    description: [
                        // Only show the cost if we have it
                        monthlyCost && yearlyCost
                            ? t`Get your child access to Khanmigo’s personalized learning for $${monthlyCost}/month or $${yearlyCost}/year to activate new learning today.`
                            : t`Get your child access to Khanmigo’s personalized learning today.`,
                    ],
                },
            ],
        },
    ] as RoleCopies;
}

// If dev'ing use the first product, otherwise use the prod Khanmigo
// product
export const khanmigoStripeProduct = RequestInfo.IS_DEV_SERVER
    ? "1"
    : "5046165006237696";

type Props = {
    role: Role;
    referral: Referral;
};

function SignupDetailScreen(props: Props) {
    const {role, referral} = props;
    const [monthlyCost, setMonthlyCost] = React.useState("");
    const [yearlyCost, setYearlyCost] = React.useState("");
    const roleCopy = getRoleCopyFor(role, monthlyCost, yearlyCost);

    // @ts-expect-error - TS2339 - Property 'data' does not exist on type 'Result<{ data }>'.
    const [{status, data}] = useQuery(PRODUCT_BY_ID_QUERY, {
        variables: {id: khanmigoStripeProduct},
        fetchPolicy: FetchPolicy.CacheAndNetwork,
        skip: !isKhanmigoReferral(referral),
    });
    if (status === "loading") {
        return null;
    }

    // NOTE: normally we'd handle the error here. But instead of showing a
    // "sorry..." message, we're updating the copy to just not include price.

    if (status === "success" && data.product?.prices) {
        if (monthlyCost === "") {
            const prices = data.product.prices.filter((price) => price != null);
            prices.sort((a, b) => a.unitAmount - b.unitAmount);
            setMonthlyCost(amountToDollars(prices[0].unitAmount));
            setYearlyCost(amountToDollars(prices[1].unitAmount));
        }
    }

    function getRoleCopyFor(
        role: Role,
        monthlyCost: string,
        yearlyCost: string,
    ): Copy {
        const placeholderCopy = {description: []} as const;
        const dataForRole = getRoleCopies(monthlyCost, yearlyCost).find(
            (obj) => obj.role === role,
        );
        if (!dataForRole) {
            // @ts-expect-error - TS2322 - Type '{ readonly description: readonly []; }' is not assignable to type 'Copy'.
            return placeholderCopy;
        }

        const copy = dataForRole.copy;
        // @ts-expect-error - TS2322 - Type 'Copy | { readonly description: readonly []; }' is not assignable to type 'Copy'.
        return (
            copy.find((c) => c.referral?.indexOf(referral) !== -1) ||
            copy.find((c) => c.referral?.indexOf("default") !== -1) ||
            placeholderCopy
        );
    }

    function getReferralStyle(referral: Referral) {
        const referralStyles = [styles.signupDetails];

        if (isLearnStormReferral(referral)) {
            referralStyles.push(styles.learnStormSignupDetails);
        } else if (isKhanmigoReferral(referral)) {
            referralStyles.push(styles.khanmigoCheckoutSignupDetails);
        }

        return referralStyles;
    }

    return (
        <View style={getReferralStyle(referral)}>
            <ProgressShellDetailScreen
                transitionKey={role}
                heading={roleCopy.header}
                optionalDetailItems={roleCopy.description}
                detailContent={
                    <TermsOfServiceSignupStatement purpose="signup" />
                }
                fontColor={isKhanmigoReferral(referral) ? "dark" : "light"}
                newLayout={true}
            />
        </View>
    );
}

const styles = StyleSheet.create({
    signupDetails: {
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",

        [mediaQueries.lgOrLarger]: {
            paddingTop: 320,
            width: "100%",
            maxWidth: 443,
            backgroundSize: "100%",
            backgroundImage: cssUrl("/images/signup/signup-banner-2x.png"),
            backgroundRepeat: "no-repeat",
            backgroundPosition: "top center",
        },
        [mediaQueries.xlOrLarger]: {
            paddingTop: 360,
        },
    },
    learnStormSignupDetails: {
        marginTop: 0,
        paddingTop: 0,
        backgroundColor: "transparent",
        width: "100%",
        display: "flex",
        justifyContent: "center",

        [mediaQueries.lgOrLarger]: {
            paddingTop: 260,
            backgroundImage: cssUrl(
                "/images/learn-storm-campaigns/learnstorm-signup-banner-2x.png",
            ),
        },
    },
    khanmigoCheckoutSignupDetails: {
        [mediaQueries.lgOrLarger]: {
            paddingTop: 100,
            backgroundSize: 104,
            backgroundImage: cssUrl("/images/khanmigo/winking_khanmigo-2x.png"),
        },
    },
});

export function amountToDollars(amount: number | undefined): string {
    if (typeof amount === "undefined") {
        return "";
    }
    return (amount / 100).toFixed(0);
}

export default SignupDetailScreen;
